import React from 'react';
import { BiPlus, BiTrash } from 'react-icons/bi';
import { TableColumnType } from '../../core/constants/common';

const createEmptyCell = (columnId) => ({ id: null, columnId, textResponse: null, numericResponse: null });

const createEmptyRow = (columns, sortOrder) => ({
	id: null,
	sortOrder,
	cells: columns.map((col) => createEmptyCell(col.id)),
});

const getSortedColumns = (content) =>
	(content?.tableConfig?.columns || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

const getCell = (row, columnId) => row.cells?.find((c) => c.columnId === columnId);

const DataTable = ({ type, content, responseData, onRowsChange, disabled = false }) => {
	const columns = getSortedColumns(content);
	const rows = responseData?.tableRowResponses ?? [];

	if (columns.length === 0) return null;

	const handleAddRow = () => {
		const nextSortOrder = (rows[rows.length - 1]?.sortOrder ?? 0) + 1;
		onRowsChange?.([...rows, createEmptyRow(columns, nextSortOrder)]);
	};

	const handleRemoveRow = (rowIndex) => {
		onRowsChange?.(rows.filter((_, i) => i !== rowIndex));
	};

	// Reject anything that isn't a (possibly partial) numeric string, so a
	// Numeric column can never end up holding text. Range (min/max) is not
	// enforced here — it's only flagged visually, see isOutOfRange below.
	const isValidNumericInput = (value) => value === '' || /^-?\d*\.?\d*$/.test(value);

	const isOutOfRange = (column, numericValue) => {
		if (numericValue === null || numericValue === undefined || numericValue === '') return false;
		const num = Number(numericValue);
		if (Number.isNaN(num)) return false;

		const min = column?.minimumValue !== null && column?.minimumValue !== undefined && column.minimumValue !== ''
			? Number(column.minimumValue) : null;
		const max = column?.maximumValue !== null && column?.maximumValue !== undefined && column.maximumValue !== ''
			? Number(column.maximumValue) : null;
		return (min !== null && num < min) || (max !== null && num > max);
	};

	// A fully-formed number gets stored as a number; an in-progress value like
	// "-" or "1." is kept as-is so the input isn't collapsed mid-keystroke.
	const toNumericResponse = (value) => {
		if (value === '') return null;
		const isCompleteNumber = /^-?\d+\.?\d*$/.test(value) && !value.endsWith('.');
		return isCompleteNumber ? Number(value) : value;
	};

	const handleCellChange = (rowIndex, columnId, columnType, value) => {
		if (columnType === TableColumnType.NUMERIC && !isValidNumericInput(value)) return;
		const updatedRows = rows.map((row, i) => {
			if (i !== rowIndex) return row;
			const cells = columns.map((col) => {
				const existing = getCell(row, col.id) || createEmptyCell(col.id);
				if (col.id !== columnId) return existing;
				return columnType === TableColumnType.NUMERIC
					? { ...existing, columnId, numericResponse: toNumericResponse(value), textResponse: null }
					: { ...existing, columnId, textResponse: value, numericResponse: null };
			});
			return { ...row, cells };
		});
		onRowsChange?.(updatedRows);
	};

	return (
		<div className='DataTableView table-responsive'>
			<table className='table table-bordered mb-2'>
				<thead>
					<tr>
						{columns.map((col) => {
							const hasMin = col.columnType === TableColumnType.NUMERIC && col.minimumValue !== null && col.minimumValue !== '';
							const hasMax = col.columnType === TableColumnType.NUMERIC && col.maximumValue !== null && col.maximumValue !== '';
							return (
								<th key={col.id ?? col.name}>
									{col.name}
									{col.required && <span className='text-danger'> *</span>}
									{(hasMin || hasMax) && (
										<div className='small text-muted fw-normal'>
											{hasMin && `Min: ${col.minimumValue}`}
											{hasMin && hasMax && ', '}
											{hasMax && `Max: ${col.maximumValue}`}
										</div>
									)}
								</th>
							);
						})}
						{type === 'SUBMIT' && <th className='DataTableView__actionCol'></th>}
					</tr>
				</thead>
				<tbody>
					{type === 'BUILD' && (
						<tr>
							{columns.map((col) => (
								<td key={col.id ?? col.name}>
									<input
										className='form-control'
										disabled
										placeholder={col.columnType === TableColumnType.NUMERIC ? '0' : 'Text'}
									/>
								</td>
							))}
						</tr>
					)}
					{type !== 'BUILD' && rows.map((row, rowIndex) => {
						const isLastRow = rowIndex === rows.length - 1;
						return (
							<tr key={row.id ?? `row-${rowIndex}`}>
								{columns.map((col) => {
									const cell = getCell(row, col.id);
									const isNumeric = col.columnType === TableColumnType.NUMERIC;
									if (type === 'SUBMIT_RESPONSE') {
										return (
											<td key={col.id ?? col.name}>
												{isNumeric ? (cell?.numericResponse ?? '') : (cell?.textResponse ?? '')}
											</td>
										);
									}
									return (
										<td key={col.id ?? col.name}>
											<input
												className={`form-control${isNumeric && isOutOfRange(col, cell?.numericResponse) ? ' is-invalid' : ''}`}
												type={isNumeric ? 'number' : 'text'}
												disabled={disabled}
												value={isNumeric ? (cell?.numericResponse ?? '') : (cell?.textResponse ?? '')}
												onChange={(e) => handleCellChange(rowIndex, col.id, col.columnType, e.target.value)}
											/>
										</td>
									);
								})}
								{type === 'SUBMIT' && (
									<td className='DataTableView__actionCol'>
										{!disabled && (
											<span className='cursor' onClick={() => handleRemoveRow(rowIndex)}>
												<BiTrash />
											</span>
										)}
									</td>
								)}
							</tr>
						);
					})}
					{type !== 'BUILD' && rows.length === 0 && (
						<tr>
							<td colSpan={columns.length + (type === 'SUBMIT' ? 1 : 0)} className='text-muted text-center'>
								No rows added
							</td>
						</tr>
					)}
				</tbody>
			</table>
			{type === 'SUBMIT' && (
				<button type='button' className='btn btn-secondary btn-sm' disabled={disabled} onClick={handleAddRow}>
					<BiPlus /> Add row
				</button>
			)}
		</div>
	);
};

export default DataTable;
